<template>
  <BasePage :back-button="true" :pageTitle=getPageTitle show-menu>
    <!-- Loading State (including PWA background recovery) -->
    <LoadingSpinner v-if="loading || isRecovering"/>

    <!-- Main Content -->
    <div v-else-if="isReady" class="d-flex flex-column overflow-auto" style="height: calc(100vh - 250px)">

      <!-- Mode Toggle -->
      <SegmentedControl
          :options="modeOptions"
          v-model="viewMode"
          class="mb-3"
      />

      <!-- Loading Mode -->
      <CoachLoadingMode
          v-if="viewMode === 'loading'"
          :ticket-data="coachTicketData"
          :remaining-passengers="remainingPassengers"
          :loaded-passengers="loadedPassengers"
          @open-passenger-quick-view="openPassengerQuickView"
      />

      <!-- Details Mode -->
      <CoachDetailsMode
          v-else
          :direction="direction.toString()"
          :coach-data="coachData"
          :passengers="passengers"
          :remaining-passengers="remainingPassengers"
          :loaded-passengers="loadedPassengers"
          :drop-off-passengers="dropOffPassengers"
          :ticket-data="coachTicketData"
          :reps="reps"
          @open-passenger-quick-view="openPassengerQuickView"
          @open-rep-quick-view="openRepQuickView"
      />

      <!-- Scanner Component -->
      <QrScannerModal
          v-if="showingScanner"
          :model-value="showingScanner"
          @close="resetState"
          @code-scanned="(code, scan) => loadPassenger('scan', code, scan)"
          @invalid-code="handleInvalidCode"
      />

      <!-- Name Search Modal -->
      <NameSearchModal
          :is-open="showingNameSearch"
          :tickets="coachTicketData?.tickets || []"
          :heading="`Find ${__DICT__.passenger}`"
          @close="closeNameSearch"
          @select="loadPassenger('manual', $event)"
          showCoachStatus
          liftPassStatusText="Loaded?"
      />

      <!-- Action Buttons -->
      <div class="fixed-bottom fixed-bottom-bar p-3">
        <!-- Initial State Buttons -->
        <template v-if="currentState === 'initial' && !showingScanner">
          <button class="btn btn-primary w-100 mb-3" @click="startScanner">
            {{ __DICT__.scanBarcode }}
          </button>
          <button class="btn btn-secondary w-100" @click="startNameSearch">
            {{ __DICT__.enterByName }}
          </button>
        </template>

        <!-- Scanner Active State Button -->
        <template v-if="showingScanner">
          <button class="btn btn-secondary w-100" @click="resetState">
            {{ __DICT__.cancel }}
          </button>
        </template>
      </div>
    </div>

    <!-- Success Modal -->
    <div class="modal modal-blur fade" :class="{ 'show': showSuccessModal }" tabindex="-1" style="display: block;"
         v-if="showSuccessModal">
      <div class="modal-dialog modal-sm modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-body">
            <div class="modal-title text-success">{{ __DICT__.success }}</div>
            <div>{{ __DICT__.loadCoachView.passengerLoaded }}</div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-success w-100" @click="acknowledgeSuccess">
              {{ __DICT__.ok }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Error Modal -->
    <div class="modal modal-blur fade" :class="{ 'show': showErrorModal }" tabindex="-1" style="display: block;"
         v-if="showErrorModal">
      <div class="modal-dialog modal-sm modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-body">
            <div class="modal-title text-danger">{{ __DICT__.error }}</div>
            <div>{{ error?.message || 'An error occurred' }}</div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-danger w-100" @click="acknowledgeError">
              {{ __DICT__.ok }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Backdrop -->
    <div class="modal-backdrop fade" :class="{ 'show': showSuccessModal || showErrorModal }"
         v-if="showSuccessModal || showErrorModal"></div>

    <!-- Passenger Quick View Modal -->
    <div class="modal modal-blur fade" :class="{ 'show': showPassengerModal }" tabindex="-1"
         style="display: block;" v-if="showPassengerModal">
      <div class="modal-dialog modal-sm modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ selectedPassenger?.reference }}</h5>
            <button type="button" class="btn-close" @click="closePassengerModal"></button>
          </div>
          <div class="modal-body">
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
            <div class="mb-2">
              <strong>{{ __DICT__.name }}:</strong> {{ selectedPassenger?.passengerName }}
            </div>
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
            <div class="mb-2" v-if="getTicketForPassenger(selectedPassenger)?.phone">
              <strong>{{ __DICT__.phone }}:</strong>
              <a href="#" @click.prevent="handlePhoneClick(getTicketForPassenger(selectedPassenger).phone)" class="text-primary">
                {{ getTicketForPassenger(selectedPassenger).phone }}
              </a>
            </div>
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
            <div class="mb-2" v-if="getTicketForPassenger(selectedPassenger)?.loadPoint">
              <strong>{{ __DICT__.loadCoachView.loadPoint }}:</strong> {{ getTicketForPassenger(selectedPassenger).loadPoint }}
            </div>
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
            <div class="mb-2" v-if="getTicketForPassenger(selectedPassenger)?.dropOffPoint">
              <strong>{{ __DICT__.dropOff }}:</strong> {{ getTicketForPassenger(selectedPassenger).dropOffPoint }}
            </div>
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
            <div class="mb-2">
              <strong>{{ __DICT__.loaded }}:</strong>
              <span :class="getTicketForPassenger(selectedPassenger)?.loaded === 1 ? 'text-success' : 'text-warning'">
                {{ getTicketForPassenger(selectedPassenger)?.loaded === 1 ? __DICT__.yes : __DICT__.no }}
              </span>
            </div>
            <div class="mb-2" v-if="selectedPassenger?.equipment">
              <span class="badge bg-primary text-white">{{ __DICT__.loadCoachView.hasEquipment }}</span>
            </div>
          </div>
          <div class="modal-footer">
            <button
                v-if="isSameWeek && can('viewTransportPassenger', {ref: selectedPassenger?.reference})"
                type="button"
                class="btn btn-primary w-100"
                @click="goToPassengerProfile"
            >
              {{ __DICT__.viewFullProfile }}
            </button>
            <button type="button" class="btn btn-secondary w-100" @click="closePassengerModal">
              {{ __DICT__.close }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade" :class="{ 'show': showPassengerModal }" v-if="showPassengerModal"></div>

    <!-- Rep Quick View Modal -->
    <div class="modal modal-blur fade" :class="{ 'show': showRepModal }" tabindex="-1" style="display: block;" v-if="showRepModal">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
            <h5 class="modal-title">{{ __DICT__.repDetails }} - {{ coachId }}</h5>
            <button type="button" class="btn-close" @click="closeRepModal"></button>
          </div>
          <div class="modal-body" v-if="selectedRepData">
            <table class="table table-vcenter">
              <tbody>
                <tr>
                  <th>{{ __DICT__.name }}</th>
                  <td>{{ selectedRepData.name }}</td>
                </tr>
                <tr v-if="selectedRepData.phone">
                  <th>{{ __DICT__.phone }}</th>
                  <td>
                    <a class="link-azure" @click="handlePhoneClick(selectedRepData.phone)">
                      {{ selectedRepData.phone }}
                    </a>
                  </td>
                </tr>
                <tr v-if="selectedRepData.resort">
                  <th>{{ __DICT__.resort }}</th>
                  <td>{{ selectedRepData.resort }}</td>
                </tr>
                <tr v-if="selectedRepData.pickup">
                  <th>{{ __DICT__.pickUp }}</th>
                  <td>{{ selectedRepData.pickup }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="modal-footer">
            <template v-if="selectedRepData?.isInCurrentWeek">
              <button type="button" class="btn btn-primary" @click="goToRepProfile">
                {{ __DICT__.viewFullProfile }}
              </button>
            </template>
            <template v-else>
              <div class="text-muted small">
                {{ __DICT__.repDifferentWeek }}
              </div>
            </template>
            <button type="button" class="btn btn-secondary" @click="closeRepModal">
              {{ __DICT__.close }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade show" v-if="showRepModal" @click="closeRepModal"></div>

    <!-- Phone Number Action Sheet -->
    <PhoneNumberActionSheet
        :is-visible="showPhoneActionSheet"
        :phone-number="selectedPhoneNumber || ''"
        @close="closePhoneActionSheet"
    />
  </BasePage>
</template>

<script setup>
import {useWeekStore} from "@/stores/week.js";
import BasePage from "@/components/BasePage.vue";
import {useRoute, useRouter} from "vue-router";
import {ref, watch, onMounted, computed, onUnmounted} from "vue";
import {rememberTicketBookings} from '@/services/qrKeyCache.js';
import {api} from "@/services/api.js";
import NameSearchModal from "@/components/NameSearchModal.vue";
import QrScannerModal from "@/components/QrScannerModal.vue";
import {qrRequestCode, qrRequestFields} from "@/utils/qrVerify.js";
import {LoadingSpinner, SegmentedControl} from "@/components/atomic";
import { usePermissions } from '@/composables/usePermissions'
import {useEnsureWeekData} from "@/composables/useEnsureWeekData.js";
import PhoneNumberActionSheet from "@/components/PhoneNumberActionSheet.vue";
import {passengerIndex} from "@/services/PassengerIndex.js";
import {useHaptics} from "@/composables/useHaptics.js";
import {useScanRedeem} from "@/composables/useScanRedeem.js";
import CoachLoadingMode from "@/components/CoachLoadingMode.vue";
import CoachDetailsMode from "@/components/CoachDetailsMode.vue";

const { can } = usePermissions()

const router = useRouter();
// Route and Store
const route = useRoute();
const weekStore = useWeekStore();

// Ensure week data is available (handles PWA background recovery)
const { isRecovering, isReady } = useEnsureWeekData();
const { errorFeedback } = useHaptics();

// View mode toggle
const viewMode = ref('loading');
const modeOptions = [
  { value: 'loading', label: 'Loading' },
  { value: 'details', label: 'Details' }
];

// State Management
const loading = ref(true);
const coachId = ref(route.params.coachId);
const direction = ref(route.params.direction);
const coachTicketData = ref({});
// The QR check can bind a scan to its ticket's booking (qr-binding-ticket-booking).
watch(() => coachTicketData.value?.tickets, rememberTicketBookings);
const coachData = ref(null);
const {processing, error, showSuccessModal, showErrorModal, redeem} = useScanRedeem({
  refresh: () => refreshData()
});
const entryMethod = ref(null);
const currentState = ref('initial');
const weekData = ref([])
const repData = ref([])
const extraData = ref({})

// Passenger Quick View Modal state
const selectedPassenger = ref(null)
const showPassengerModal = ref(false)
const showPhoneActionSheet = ref(false)
const selectedPhoneNumber = ref(null)

// Rep Quick View Modal state
const showRepModal = ref(false)
const selectedRepData = ref(null)

const getPageTitle = computed(() =>
    `${coachId.value} | ${direction.value}`
)

// Coach loading is always in current week context
const isSameWeek = computed(() => true)

// Get ticket data for a passenger by reference
const getTicketForPassenger = (passenger) => {
  if (!coachTicketData.value?.tickets) return null
  return coachTicketData.value.tickets.find(
    ticket => ticket.bookingRef === passenger.reference
  )
}

const openRepQuickView = (rep) => {
  const userId = rep['GBS ID']
  const name = rep.Name

  // Check if this rep exists in the Pinia store (i.e., is in current week)
  const storeRep = (weekStore.repData ?? []).find(r => String(r['GBS ID']) === String(userId))

  selectedRepData.value = {
    name: name,
    userId: userId,
    phone: rep['Phone Number'] || null,
    resort: rep.Resort || null,
    pickup: repPickup(rep),
    isInCurrentWeek: !!storeRep
  }

  showRepModal.value = true
}

const closeRepModal = () => {
  showRepModal.value = false
  selectedRepData.value = null
}

const goToRepProfile = () => {
  if (selectedRepData.value?.userId) {
    router.push(`/rep/${selectedRepData.value.userId}`)
    closeRepModal()
  }
}

const repPickup = (rep) => {
  if (direction.value === 'outbound') {
    return rep['OB Pickup Point']
  } else {
    return rep['IB Pickup Point']
  }
}

// Computed Properties
const showingScanner = computed(() =>
    currentState.value === 'initial' && entryMethod.value === 'scan'
);

const showingNameSearch = computed(() =>
    currentState.value === 'initial' && entryMethod.value === 'manual'
);

// Methods
const generateGuid = () => {
  const s4 = () => Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
};

// Open passenger quick view modal
const openPassengerQuickView = (passenger) => {
  selectedPassenger.value = passenger
  showPassengerModal.value = true
}

const closePassengerModal = () => {
  showPassengerModal.value = false
  selectedPassenger.value = null
}

const handlePhoneClick = (phone) => {
  selectedPhoneNumber.value = phone
  showPhoneActionSheet.value = true
}

const closePhoneActionSheet = () => {
  showPhoneActionSheet.value = false
  selectedPhoneNumber.value = null
}

const goToPassengerProfile = async () => {
  const passenger = selectedPassenger.value
  if (passenger && isSameWeek.value && can('viewTransportPassenger', {ref: passenger.reference})) {
    closePassengerModal()
    await router.push(`/passenger/${passenger.reference}`)
  }
}

const linkToPassenger = (passenger) => {
  openPassengerQuickView(passenger)
}

// scan: the scanner's { raw, token, verification } - absent for name search.
const loadPassenger = async (method, data, scan = null) => {
  if (processing.value) return;

  processing.value = true;
  error.value = null;

  // Guard against null repObj (can happen when PWA resumes from background)
  if (!weekStore.repObj) {
    error.value = { message: 'Session data not available. Please go back and try again.' };
    showErrorModal.value = true;
    processing.value = false;
    return;
  }

  try {
    const payload = {
      weekID: weekStore.weekId,
      barcode: qrRequestCode(scan, data),
      ...qrRequestFields(scan, data),
      handsetTimestamp: new Date().toISOString(),
      idempotentId: generateGuid(),
      repID: weekStore.repObj.repId,
      repName: weekStore.repObj.name,
      scanned: method === 'scan' ? 1 : 0,
      coachId: coachId.value,
      direction: direction.value
    };

    resetState();
    await redeem('/api/v3/scan/coach', payload);
  } catch (e) {
    error.value = e;
    resetState();
    showErrorModal.value = true;
    errorFeedback();
  } finally {
    processing.value = false;
    resetState();
  }
};

const startScanner = () => {
  entryMethod.value = 'scan';
};

const startNameSearch = () => {
  entryMethod.value = 'manual';
};

const closeNameSearch = () => {
  resetState();
};

const reps = computed(() => {
  // Use reps from extraData API (merged from all weeks with same GBS_WEEK_ID)
  if (extraData.value?.reps?.length > 0) {
    return extraData.value.reps
  }
  // Fallback to filtering from store repData
  let dir = "OB"
  if (direction.value === 'inbound') {
    dir = "IB"
  }
  const repList = repData.value ?? []
  if (!Array.isArray(repList)) return []
  return repList.filter(rep => rep[`${dir} Transport`] === route.params.coachId)
})


const resetState = () => {
  entryMethod.value = null;
  processing.value = false;
  currentState.value = 'initial';
};

const acknowledgeSuccess = () => {
  showSuccessModal.value = false;
};

const acknowledgeError = () => {
  showErrorModal.value = false;
  error.value = null;
};

const handleInvalidCode = () => {
  error.value = {message: 'Invalid barcode format'};
  showErrorModal.value = true;
  errorFeedback();
};

let refreshTimeout = null;

const refreshData = async () => {
  coachTicketData.value = await api.get(`/api/coach/allLiveBarcodes/${coachId.value}/direction/${direction.value}/${weekStore.systemId}`);
  extraData.value = await api.get(`/api/coach/extraData/${direction.value}/${coachId.value}/${weekStore.systemId}`);
};

const refreshDataHook = () => {
  refreshData();
  refreshTimeout = setTimeout(refreshDataHook, 10000);
};

const passengers = computed(() => {
  if (!coachTicketData.value?.tickets) return [];

  const passengersByLocation = coachTicketData.value.tickets.reduce((acc, ticket) => {
    const location = ticket.loadPoint;

    if (!location) return acc;

    if (!acc[location]) {
      acc[location] = {
        loadedPassengers: [],
        remainingPassengers: []
      };
    }

    // Use indexed lookup for O(1) performance
    const weekDataPassenger = passengerIndex.getByReference(ticket.bookingRef);
    const place = weekDataPassenger?.allocPlaces?.find(place =>
        place.transport?.transportType === "coach_travel" &&
        place.transport?.coach === coachId.value &&
        place.transport?.type === `transport-${direction.value}`
    );
    const equipmentCarriage = place?.transport?.equipmentCarriage || '';
    const hasEquipment = equipmentCarriage.startsWith('I want');

    // Create a passenger object combining ticket data with equipment flag
    const passengerObj = {
      reference: ticket.bookingRef,
      passengerName: ticket.name,
      phone: ticket.phone,
      equipment: hasEquipment
    };

    if (ticket.loaded === 1) {
      acc[location].loadedPassengers.push(passengerObj);
    } else {
      acc[location].remainingPassengers.push(passengerObj);
    }

    return acc;
  }, {});

  // Transform into final format
  return Object.entries(passengersByLocation).map(([location, {loadedPassengers, remainingPassengers}]) => ({
    location,
    loadedPassengers,
    remainingPassengers
  }));
});

const remainingPassengers = computed(() => {
  if (!coachTicketData.value?.tickets) return [];

  return coachTicketData.value.tickets
      .filter(ticket => ticket.loaded === 0)
      .map(ticket => {
        const weekDataPassenger = passengerIndex.getByReference(ticket.bookingRef);
        const place = weekDataPassenger?.allocPlaces?.find(place =>
            place.transport?.transportType === "coach_travel" &&
            place.transport?.coach === coachId.value &&
            place.transport?.type === `transport-${direction.value}`
        );
        const equipmentCarriage = place?.transport?.equipmentCarriage || '';
        const hasEquipment = equipmentCarriage.startsWith('I want');

        return {
          ...ticket,
          reference: ticket.bookingRef,
          passengerName: ticket.name,
          equipment: hasEquipment
        };
      });
});

const loadedPassengers = computed(() => {
  if (!coachTicketData.value?.tickets) return [];

  return coachTicketData.value.tickets
      .filter(ticket => ticket.loaded === 1)
      .map(ticket => {
        const weekDataPassenger = passengerIndex.getByReference(ticket.bookingRef);
        const place = weekDataPassenger?.allocPlaces?.find(place =>
            place.transport?.transportType === "coach_travel" &&
            place.transport?.coach === coachId.value &&
            place.transport?.type === `transport-${direction.value}`
        );
        const equipmentCarriage = place?.transport?.equipmentCarriage || '';
        const hasEquipment = equipmentCarriage.startsWith('I want');

        return {
          ...ticket,
          reference: ticket.bookingRef,
          passengerName: ticket.name,
          equipment: hasEquipment
        };
      });
});

const dropOffPassengers = computed(() => {
  if (!coachTicketData.value?.tickets) return [];

  const passengersByLocation = coachTicketData.value.tickets.reduce((acc, ticket) => {
    const location = ticket.dropOffPoint;

    if (!location) return acc;

    if (!acc[location]) {
      acc[location] = {
        loadedPassengers: [],
        remainingPassengers: []
      };
    }

    const passengerObj = {
      reference: ticket.bookingRef,
      passengerName: ticket.name,
    };

    if (ticket.loaded === 1) {
      acc[location].loadedPassengers.push(passengerObj);
    } else {
      acc[location].remainingPassengers.push(passengerObj);
    }

    return acc;
  }, {});

  return Object.entries(passengersByLocation).map(([location, {loadedPassengers, remainingPassengers}]) => ({
    location,
    loadedPassengers,
    remainingPassengers
  }));
});

// Initial Data Load
onMounted(async () => {
  weekData.value = weekStore.weekData;
  repData.value = weekStore.repData;
  try {
    const [, coachDataResult] = await Promise.all([
      refreshData(),
      api.get(`/api/coach/coachData/${weekStore.weekId}`).catch(() => null)
    ]);

    if (coachDataResult?.mergedData) {
      coachData.value = coachDataResult.mergedData.find(
          coach => coach.COACH === coachId.value
      ) || null;
    }

    refreshDataHook();
  } finally {
    loading.value = false;
  }
});

onUnmounted(() => {
  if (refreshTimeout) {
    clearTimeout(refreshTimeout);
  }
});

</script>
